/* eslint-disable  @typescript-eslint/ban-ts-comment */
import { MapStyle } from 'react-map-gl/maplibre';
import bbox from '@turf/bbox';
import circle from '@turf/circle';
import distance from '@turf/distance';
import midpoint from '@turf/midpoint';
import destination from '@turf/destination';
import bearing from '@turf/bearing';
import intersect from '@turf/intersect';
import turfArea from '@turf/area';

import type { Polygon, Feature, GeoJsonProperties } from 'geojson'; // as some type export moved to separate package since v7
import { featureCollection, polygon as turfPolygon, Units, AllGeoJSON } from '@turf/helpers';

// eslint-disable-next-line import/no-unresolved
import { TileLayer } from '@deck.gl/geo-layers';
import { RGBAColor, Position as DeckGlPosition } from '@deck.gl/core';
import { BitmapLayer } from '@deck.gl/layers';
import centerOfMass from '@turf/center-of-mass';
import WebMercatorViewport from 'viewport-mercator-project';
import {
  DataItem,
  PropLocationItem,
  SensorLatest,
  GeoJSON,
  Position,
  Detail,
  DataTreeItem,
} from '../../services/api';
import { dataColours, getDataBandParams, varNameBandParams } from '../../utils/dataBandParams';
import { VarName } from '../../utils/varNames';
import ICON_ATLAS from './icon-atlas.png';
import { isDataExpired } from '../../utils/functions';
import { getMotionUtlBandParams } from '../CalendarView/helpers';
import { ThemeMode } from '../../state/types';
import { DeckGLViewPort } from './types';
import { BandValues } from '../SensorArrayWidgets/base';
import { themeProps } from '../../styles/theme';
import { getDataValueNumber } from '../HelperComponents/DataValueString';

export const MAX_FLOOR_OBJECT_ELEVATION = 1; // in metres
export const MAX_SENSOR_ELEVATION = 5; // in metres
export const FLOORPLAN_ZOOM = 5;
export const MIN_ZOOM = 0;
export const MAX_ZOOM = 21;
export const MAP_DEFAULT_PITCH = 15;

export enum HoverType {
  list = 'list',
  map = 'map',
}

export interface HoveredLoc {
  id: string;
  hoveredType: HoverType;
}

export { ICON_ATLAS }; // = './icon-atlas.png';
export const ICON_MAPPING = {
  marker: {
    x: 128,
    y: 0,
    width: 128,
    height: 128,
    mask: true,
    anchorY: 128,
  },
  sensor: {
    x: 512,
    y: 0,
    width: 128,
    height: 128,
    mask: true,
    anchorY: 110,
    anchorX: 60,
  },
  'toilet.m': {
    x: 0,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 64,
  },
  'toilet.f': {
    x: 0,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 64,
  },
  'toilet.d': {
    x: 0,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 64,
  },
  toilet: {
    x: 0,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 64,
  },
  lift: {
    x: 128,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 64,
  },
  stairs: {
    x: 256,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 64,
  },
  unknown: {
    x: 0,
    y: 0,
    width: 1,
    height: 1,
    mask: false,
    anchorY: 0,
  },
  person: {
    x: 256,
    y: 0,
    width: 128,
    height: 128,
    mask: true,
    anchorY: 128,
  },
  building: {
    x: 384,
    y: 0,
    width: 128,
    height: 128,
    mask: false,
    anchorY: 90,
    anchorX: 140,
  },
  personCircle: {
    x: 383,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
  },
  circleWithDot: {
    x: 504,
    y: 128,
    width: 128,
    height: 128,
    mask: false,
  },
  circle: {
    x: 0,
    y: 256,
    width: 128,
    height: 128,
    mask: false,
  },
};

// Align floorplan on pages, for instance active Source has floorplan aligned to right side
export enum MapCentrePosition {
  default = 'default',
  right = 'right',
  left = 'left',
  bottom = 'bottom',
}

export interface LocationStateProps {
  latitude: number;
  longitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
  transitionDuration: number;
  maxZoom: number;
}

export interface MarkerData {
  id: string;
  name: string;
  shortName: string;
  position: Position;
  value: number;
  time: number;
  varName: VarName;
  isOnline: boolean;
  detail: Detail | null;
}

export const defaultLocation: LocationStateProps = {
  latitude: 53,
  longitude: -1,
  zoom: 4,
  pitch: 0,
  bearing: 0,
  transitionDuration: 500,
  maxZoom: MAX_ZOOM,
};

export enum FloorPlanItemType {
  floor = 'area.floor',
  room = 'area.room',
  desk = 'furniture.desk',
  chair = 'furniture.chair',
  wall = 'wall',
  window = 'window',
}

export function getFloorPlanItemFillColour(item: string): RGBAColor {
  switch (item as FloorPlanItemType) {
    // Try to avoid opacity 255 as it hides everything infront/behind it
    case FloorPlanItemType.wall:
    case FloorPlanItemType.floor:
      return [249, 246, 239, 80];
    case FloorPlanItemType.window:
      return [52, 183, 235, 50];
    case FloorPlanItemType.room:
      return [0, 0, 0, 0];
    case FloorPlanItemType.desk:
    case FloorPlanItemType.chair:
    default:
      return [179, 179, 179, 200];
  }
}

export function getFloorPlanItemLineColour(item: string): RGBAColor {
  switch (item as FloorPlanItemType) {
    case FloorPlanItemType.wall:
    case FloorPlanItemType.room:
      return [128, 128, 128, 255];
    case FloorPlanItemType.window:
      return [52, 183, 235, 50];
    case FloorPlanItemType.floor:
      return [0, 0, 0, 255];
    case FloorPlanItemType.desk:
    case FloorPlanItemType.chair:
    default:
      return [179, 179, 179, 255];
  }
}

export function getFloorPlanItemElevation(item: string): number {
  switch (item as FloorPlanItemType) {
    case FloorPlanItemType.desk:
    case FloorPlanItemType.chair:
      return 1;
    case FloorPlanItemType.wall:
    case FloorPlanItemType.window:
      return 2.5;
    case FloorPlanItemType.floor:
      return 0;
    case FloorPlanItemType.room:
      return 0.9;
    default:
      return 0.5;
  }
}

export interface PolygonMarker {
  id: string;
  contours: DeckGlPosition[];
  name: string;
  value: number;
  varName: VarName;
  time: number;
}

export type Marker = {
  name: string;
  id: string;
  location: string;
  coordinates: Array<number | undefined>;
  availableData: DataItem[];
  color?: RGBAColor;
  icon?: string;
};

export function hexToRgb(hex: string, alpha?: number): RGBAColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    return [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
      alpha ?? 128,
    ];
  }

  return [52, 95, 145, alpha ?? 128];
}

export function getMarkerColour(
  value: number,
  time: number,
  addTransparency: boolean,
  activeMarker: VarName
): RGBAColor {
  let alpha = 255;

  if (addTransparency) {
    alpha = 128;
  }

  // Highlight items with no recent data (likely offline)
  if (
    activeMarker !== VarName.OnlineStatus &&
    activeMarker !== VarName.MotionEvent &&
    time &&
    isDataExpired(time)
  ) {
    return [151, 155, 166, alpha];
  }
  // Can we find a colour
  const c = getDataBandParams(activeMarker, value ?? NaN);
  return hexToRgb(c?.color, alpha); // if needed a default can be returned as [52, 95, 145, alpha]
}

const mapTilesLight = [
  'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  'https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
];

const mapTilesDark = [
  'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  'https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
];

export const getMapLibreStyle = (themeMode: ThemeMode): MapStyle => ({
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: themeMode === ThemeMode.dark ? mapTilesDark : mapTilesLight,
      tileSize: 256,
      maxzoom: 19,
    },
  },
  layers: [
    {
      id: 'osm',
      type: 'raster',
      source: 'osm', // This must match the source key above
    },
  ],
});
// tile layer is only used in positioning map now, should be updated to react-map-gl at some point
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createTileLayer = (
  themeMode: ThemeMode,
  ref: string,
  onClick?: (o?: unknown, e?: unknown) => unknown | undefined
) => {
  const mapTiles = themeMode === ThemeMode.dark ? mapTilesDark : mapTilesLight;
  const newLayer = new TileLayer({
    id: `tile-layer-${ref}`,
    data: mapTiles,
    pickable: onClick !== undefined,
    // onViewportLoad: onTilesLoad,
    autoHighlight: false,
    highlightColor: [60, 60, 60, 40],
    minZoom: 0,
    tileSize: 256,
    onClick,
    renderSubLayers: (props) => {
      const {
        // eslint-disable-next-line react/prop-types
        bbox: { west, south, east, north },
        // eslint-disable-next-line react/prop-types
      } = props.tile;

      return [
        new BitmapLayer(props, {
          data: undefined,
          image: props.data,
          bounds: [west, south, east, north],
        }),
      ];
    },
  });

  return newLayer;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getCentreOfObject = (
  geometry: any,
  floorPlan?: GeoJSON,
  polygonId?: number | string
): DeckGlPosition => {
  let coords = geometry.coordinates;
  // For some reason Turfjs centerOfMass doesn't work with MultiPolygon
  if (geometry.type === 'MultiPolygon') {
    [coords] = coords;
  }
  const poly = turfPolygon(coords);
  let center = centerOfMass(poly);

  // in some cases, there are combined polygon with same id, for instance motion chair/desk and marker is picking its position randomly
  // to avoid that, get center of mass for the combined polygon
  if (polygonId) {
    const polygonData =
      polygonId &&
      floorPlan?.features?.find((item) => item?.properties?.id === polygonId)?.geometry.coordinates;

    // Ensure polygonData is an array of arrays of rings (GeoJSON style)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formattedPolygons = polygonData ? polygonData.map((ring: any) => turfPolygon(ring)) : [];

    if (formattedPolygons.length > 1) {
      // Initialize with first polygon
      let largestPolygon = formattedPolygons.length > 0 && formattedPolygons[0];
      let largestArea: number = largestPolygon && turfArea(largestPolygon);

      // eslint-disable-next-line @typescript-eslint/no-shadow , @typescript-eslint/no-explicit-any
      formattedPolygons?.forEach((poly: any) => {
        const area = poly && turfArea(poly);
        if (area && area > largestArea) {
          largestArea = area;
          largestPolygon = poly;
        }
      });
      center = largestPolygon && centerOfMass(largestPolygon);
    } else center = centerOfMass(formattedPolygons[0]);
  }

  return [
    center.geometry.coordinates[0],
    center.geometry.coordinates[1],
    MAX_FLOOR_OBJECT_ELEVATION,
  ];
};

export const calculateViewSize = (
  childLocations: PropLocationItem[],
  childSensors: SensorLatest[],
  currentLocation: string,
  floorPlan: GeoJSON | undefined,
  viewState: DeckGLViewPort
): DeckGLViewPort | undefined => {
  const isBuilding =
    floorPlan &&
    floorPlan.features.filter((feature) => feature.properties.type === 'area.floor').length > 1;
  const defaultZoom = 18;

  const currentLocationCoordinates = {
    minLat: 90,
    minLon: 180,
    maxLat: -90,
    maxLon: -180,
  };

  let processed = false;
  let newViewState;

  // Determine max extent of location and sensor markers
  const childLocationsAndSensors = [...childLocations, ...childSensors];
  childLocationsAndSensors.forEach((item) => {
    const loc = item.location ?? '';

    if (loc.indexOf(currentLocation) !== -1) {
      if (item.position?.lat !== undefined && item.position?.lng !== undefined) {
        const lng = item.position.lng as number;
        const lat = item.position.lat as number;
        if (lng < currentLocationCoordinates.minLon) {
          currentLocationCoordinates.minLon = lng;
        }
        if (lng > currentLocationCoordinates.maxLon) {
          currentLocationCoordinates.maxLon = lng;
        }
        if (lat < currentLocationCoordinates.minLat) {
          currentLocationCoordinates.minLat = lat;
        }
        if (lat > currentLocationCoordinates.maxLat) {
          currentLocationCoordinates.maxLat = lat;
        }

        processed = true;
      }
    }
  });

  // Determine extent of floorplan
  if (floorPlan && floorPlan.features.length > 0) {
    const [minLng, minLat, maxLng, maxLat] = bbox(floorPlan as AllGeoJSON); // Turf.js
    if (minLng < currentLocationCoordinates.minLon) {
      currentLocationCoordinates.minLon = minLng;
    }
    if (maxLng > currentLocationCoordinates.maxLon) {
      currentLocationCoordinates.maxLon = maxLng;
    }
    if (minLat < currentLocationCoordinates.minLat) {
      currentLocationCoordinates.minLat = minLat;
    }
    if (maxLat > currentLocationCoordinates.maxLat) {
      currentLocationCoordinates.maxLat = maxLat;
    }

    processed = true;
  }

  if (processed) {
    const { minLon, maxLon, minLat, maxLat } = currentLocationCoordinates;
    const { longitude, latitude, zoom } = new WebMercatorViewport({
      width: 800,
      height: 600,
    }).fitBounds([
      [minLon, minLat],
      [maxLon, maxLat],
    ]);
    newViewState = {
      ...viewState,
      latitude,
      longitude,
      zoom: isBuilding || (minLon === maxLon && minLat === maxLat) ? defaultZoom : zoom, // zoom out if location extent cannot be determined
    };
  }
  return newViewState;
};

export interface MotionMapRecord {
  occupancyValue: number;
  id: string;
  online: boolean;
  color: string;
}

export const getMotionMap = (
  markerData: MarkerData[],
  isUtlData: boolean
): Map<string, MotionMapRecord | null> | null => {
  const map = new Map<string, MotionMapRecord | null>();
  markerData?.forEach((marker) => {
    const { isOnline, position, id, value, varName } = marker;
    const polygon = position?.polygon;
    // if there is more than one sensor for the polygon ignore the data (use coverage layer)
    if (polygon && map.has(polygon)) {
      map.set(polygon, null);
    } else if (varName === VarName.MotionEvent && polygon) {
      map.set(polygon, {
        occupancyValue: value,
        id,
        online: isOnline,
        color: isUtlData
          ? getMotionUtlBandParams(value)?.color ?? themeProps.colors.darkGrey
          : getDataBandParams(VarName.MotionEvent, value)?.color ?? themeProps.colors.darkGrey,
      });
    }
  });

  return map;
};

export const getCirclePolygon = (markerData: MarkerData[], marker: MarkerData) => {
  let radius = 5;
  const { id, position, varName } = marker;
  if (varName === VarName.ClientsBle || varName === VarName.ClientsWiFi) radius = 10;
  const targetPoint = [position?.lng ?? 0, position?.lat ?? 0];
  const options = { steps: 50, units: 'meters' as Units };
  let circlePolygon = circle(targetPoint, radius, options);
  const overlappingSensors: SensorLatest[] = [];
  markerData?.forEach((dataItem) => {
    const center = [dataItem.position?.lng ?? 0, dataItem.position?.lat ?? 0];
    const targetDistance = distance(targetPoint, center, { units: 'meters' });
    if (targetDistance < radius * 2 && dataItem.id !== id) {
      if (varName === VarName.ClientsBle || varName === VarName.ClientsWiFi) {
        overlappingSensors.push(dataItem);
      } else if (dataItem.position?.polygon === position?.polygon)
        overlappingSensors.push(dataItem);
    }
  });
  if (overlappingSensors.length > 0) {
    for (let i = 0; i < overlappingSensors.length; i++) {
      const center = [
        overlappingSensors[i].position?.lng ?? 0,
        overlappingSensors[i].position?.lat ?? 0,
      ];
      const midPoint = midpoint(center, targetPoint).geometry.coordinates;
      const bearingAngle = bearing(targetPoint, center);
      const bearingOptions = { units: 'meters' as Units };
      const destination1 = destination(midPoint, radius, bearingAngle + 90, bearingOptions);
      const destination2 = destination(
        destination1.geometry.coordinates,
        radius * 2,
        bearingAngle + 180,
        bearingOptions
      );
      const destination3 = destination(
        destination2.geometry.coordinates,
        radius * 2,
        bearingAngle + 270,
        bearingOptions
      );
      const destination4 = destination(
        destination3.geometry.coordinates,
        radius * 2,
        bearingAngle + 360,
        bearingOptions
      );
      const destinationPolygon = turfPolygon([
        [
          destination1.geometry.coordinates,
          destination2.geometry.coordinates,
          destination3.geometry.coordinates,
          destination4.geometry.coordinates,
          destination1.geometry.coordinates,
        ],
      ]);

      circlePolygon = intersect(featureCollection([circlePolygon, destinationPolygon])) as Feature<
        Polygon,
        GeoJsonProperties
      >;
    }
  }
  return circlePolygon;
};

export function weightedColorWithBands(
  varName: VarName,
  onlineVarData: DataTreeItem[],
  locId: string,
  opacity: number,
  motionThreshold: [number, number]
) {
  if (!locId) return hexToRgb(themeProps.colors.baseGrey, opacity);

  const varNameBand = varNameBandParams[varName] ?? [];

  // create band sum with number of sensors available in each band
  const perBandSums = (loc: string) => {
    const result = varNameBand?.map(({ color }) => ({
      total: 0,
      pct: 0,
      colour: color,
    })) as BandValues[];

    const locVarData = onlineVarData.filter(
      (item) => loc && (item.location === loc || item?.location?.startsWith(`${loc}#`))
    );

    locVarData.forEach((dataItem) => {
      const value = getDataValueNumber(dataItem, motionThreshold);
      for (let i = 0; i < result.length; i++) {
        if ((value ?? Infinity) <= varNameBand[i].upperBound) {
          result[i].total += 1;
          break;
        }
      }
    });

    result.forEach((item, index) => {
      result[index].pct = (100 * item.total) / locVarData.length;
    });
    return result;
  };

  let totalWeight = 0;
  let totalR = 0;
  let totalG = 0;
  let totalB = 0;

  // combine band colors based on number of sensors (percentage) in each band
  perBandSums(locId).forEach((item) => {
    if (item.pct === 0) return;

    // Convert hex to RGB
    const rgb = hexToRgb(item.colour);
    if (!rgb) return;
    totalR += rgb[0] * item.pct;
    totalG += rgb[1] * item.pct;
    totalB += rgb[2] * item.pct;

    // Add to total weight
    totalWeight += item.pct;
  });

  // Normalize by total weight
  if (totalWeight > 0) {
    const r = Math.round(totalR / totalWeight);
    const g = Math.round(totalG / totalWeight);
    const b = Math.round(totalB / totalWeight);
    return [r, g, b, opacity];
  }
  return hexToRgb(themeProps.colors.mediumGrey, opacity); // Default if no valid colors
}

// generate weight color for source with missing band
export const weightedColorWithoutBands = (
  locId: string,
  childLocations: PropLocationItem[],
  onlineVarData: DataTreeItem[],
  opacity: number
) => {
  let minValue: number | undefined;
  let maxValue: number | undefined;

  // helper to get total value for location
  const getTotal = (id: string) => {
    let total = 0;
    const locVarData = onlineVarData.filter((item) => item?.location?.startsWith(id));
    locVarData.forEach((item) => {
      // Don't include old data
      if (isDataExpired(item.time)) return;
      if (item.value === 0) return;
      total += item.value ?? 0;
    });

    return total;
  };

  // find min and max value detected in that specific location
  childLocations.forEach((location) => {
    const total = getTotal(location.id);
    if (total && (!minValue || total < minValue)) minValue = total;
    if (total && (!maxValue || total > maxValue)) maxValue = total;
  });

  // generate band based on just min and max value
  const varNameBand = [
    {
      upperBound: minValue ?? 0,
      color: dataColours.blue,
      text: 'Low count',
      label: 'Low count',
    },
    {
      upperBound: maxValue ?? 0,
      color: dataColours.green,
      text: 'Max count',
      label: 'Max count',
    },
  ];

  // get sum from sensors and calculate value weighted color based on combination of min and max color
  // needed to use sum value here because all sensors are already above the min value reading
  if (getTotal(locId) > 0 && varNameBand && varNameBand.length >= 2) {
    const combinedHex = [0, 2, 4]
      .map((i) =>
        Math.round(
          parseInt(varNameBand[0].color.substr(i + 1, 2), 16) +
            (parseInt(varNameBand[1].color.substr(i + 1, 2), 16) -
              parseInt(varNameBand[0].color.substr(i + 1, 2), 16)) *
              Math.min(
                1,
                Math.max(
                  0,
                  (getTotal(locId) - varNameBand[0].upperBound) /
                    (varNameBand[1].upperBound - varNameBand[0].upperBound)
                )
              )
        )
          .toString(16)
          .padStart(2, '0')
      )
      .join('');
    return hexToRgb(`#${combinedHex}`, opacity);
  }
  return hexToRgb(themeProps.colors.mediumGrey, opacity); // Default if no valid colors
};

export function findNearestPointsWithTurf(
  targetPos: DeckGlPosition,
  floorPositions: DeckGlPosition[]
) {
  let minDistance = Infinity;
  let endPoint = null;
  let newPosition;
  const targetDistance = 2;

  // Compare each target point with floorPositions
  for (let i = 0; i < floorPositions.length; i++) {
    // Calculate distance using turf.distance (returns distance in kilometers by default)
    const totaldistance = distance(targetPos, floorPositions[i], { units: 'meters' });

    if (totaldistance < minDistance) {
      minDistance = totaldistance;
      endPoint = floorPositions[i];
    }
  }

  if (endPoint) {
    // Calculate the bearing (angle) from start to end point
    // Bearing is measured in degrees from north (0°), clockwise
    const bearingAngle = bearing(targetPos, endPoint);

    // Create a new position by moving from the end point in the same direction
    // Using turf.destination to move the specified distance at the calculated bearing
    const newPoint = destination(endPoint, targetDistance, bearingAngle, { units: 'meters' });
    newPosition = newPoint.geometry.coordinates as DeckGlPosition;
  }

  return newPosition;
}

export interface SpaceParams {
  title: string;
  sensors: string[];
}

export const getAllSpaces = (sensorsInLoc: SensorLatest[]) => {
  if (!sensorsInLoc.length) return undefined;
  // Map to group sensors by their tags
  const spaceMap = new Map<string, string[]>();

  // Process each sensor and group by tags
  sensorsInLoc.forEach((sensor: SensorLatest) => {
    // For each groupTag in the sensor, add the sensor to that space
    sensor?.tags?.forEach((tag) => {
      if (!spaceMap.has(tag)) {
        spaceMap.set(tag, []);
      }
      spaceMap.get(tag)?.push(sensor.id);
    });
  });

  // Convert the map to SpaceParams array
  const spaces: SpaceParams[] = Object.entries(Object.fromEntries(spaceMap))
    .map(([title, sensors]) => ({
      title,
      sensors,
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

  return spaces.length > 0 ? spaces : undefined;
};
